import Page from 'src/components/Page';
import {
    Autocomplete,
    Card, CardContent, Container,
    Stack, TextField, Switch
}
    from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useFormik, Form, FormikProvider, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import * as constants from 'src/utils/constants';
import { apiInstance } from 'src/utils/apiAuth';
import { showErrorToast, showToast } from 'src/utils/Toast/SnackbarProvider';
import { useEffect, useState } from 'react';
import { capitalizeWords } from 'src/utils/commonUtils';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

// Register the English locale for country names
countries.registerLocale(enLocale);

// Dynamic helper function to get ISO country code from country name
const getCountryISOCode = (countryName) => {
    if (!countryName) return 'in';
    
    // Try to get alpha-2 code directly by name
    const code = countries.getAlpha2Code(countryName, 'en');
    
    if (code) return code.toLowerCase();
    
    // If direct lookup fails, try fuzzy matching
    const normalized = countryName.toLowerCase().trim();
    const allCountries = countries.getNames('en');
    
    // Find country by partial match
    const match = Object.entries(allCountries).find(([code, name]) => 
        name.toLowerCase() === normalized ||
        name.toLowerCase().includes(normalized) ||
        normalized.includes(name.toLowerCase())
    );
    
    return match ? match[0].toLowerCase() : 'in';
};

export default function AddCompanyMembersForm() {

    const hrToken = localStorage.getItem("authToken");
    const navigate = useNavigate();

    // State for phone country code
    const [phoneCountryCode, setPhoneCountryCode] = useState('in');

    const [cmdData, setCMDData] = useState([]);
    const [locationData, setLocationData] = useState([]);
    const getCMD = () => {
        const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company-member-designation?`
        apiInstance.get(apiUrl, { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } })
        .then((response) => {
            if (response?.data) {
                const activeDesignations = response?.data?.data?.filter(obj => obj?.is_active) || [];
                const otherOption = { id: 'other', label: 'Other', is_active: true };
                setCMDData([...activeDesignations, otherOption]);
            }
        })
        .catch(error => {
            if (error) {
                showErrorToast(error);
            }
        });
    }

    const getCountryData = () => {
        const apiUrl = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/location?`
        apiInstance.get(apiUrl, { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } })
            .then((response) => {
                if (response?.data) {
                    setLocationData(response?.data?.data?.filter(obj => obj?.is_active) || []);
                }
            })
            .catch(error => {
                if (error) {
                    showErrorToast(error);
                }
            });
    }

    useEffect(() => {
        getCMD();
        getCountryData();
    }, [])

    const callAddCompanyMembersAPI = (formValues) => {
        apiInstance({
            method: "post",
            url: "company-members/",
            headers: {
                Authorization: "token " + hrToken,
            },
            data: formValues,
        })
            .then(function (response) {
                showToast(response.data.message)
                navigate("/company-members", { replace: true });
            })
            .catch(function (error) {
                showErrorToast(error);
                setSubmitting(false)
            });
    }

    const mobileRegExp = /^[0-9]{10,15}$/;

    const Schema = Yup.object().shape({
        first_name: Yup.string()
            .min(2, "Too Short!")
            .max(50, "Too Long!")
            .matches(/^[a-zA-Z]+$/, "Only alphabets without spaces are allowed for first name ")
            .required("First name required"),
        middle_name: Yup.string(),
        last_name: Yup.string()
            .min(1, "Too Short!")
            .max(50, "Too Long!")
            .matches(/^[a-zA-Z]+$/, "Only alphabets without spaces are allowed for last name ")
            .required("Last name required"),
        email: Yup.string()
            .required("Email required"),
        country: Yup.string(),
        mobile: Yup.string()
            .matches(mobileRegExp, 'Mobile number not valid'),
        linkedin_profile: Yup.string()
            .required("LinkedIn profile required"),
        cmd: Yup.array()
            .min(1, "Designation required"),
        is_active: Yup.string()
            .required("Active status required."),
    });

    const formik = useFormik({
        initialValues: {
            first_name: '',
            middle_name: '',
            last_name: '',
            email: '',
            country: '',
            mobile: '',
            linkedin_profile: '',
            cmd: '',
            is_active: '',
        },
        validationSchema: Schema,
        onSubmit: (values) => {
            let formData = {
                "first_name": values.first_name,
                "middle_name": values.middle_name,
                "last_name": values.last_name,
                "email": values.email,
                "country": values.country,
                "mobile": values.mobile,
                "linkedin_profile": values.linkedin_profile,
                "cmd": values.cmd,
                "is_active": values.is_active,
            }
            callAddCompanyMembersAPI(formData)
        }
    });

    const handleNameChange = (field) => (event) => {
        const capitalizedValue = capitalizeWords(event.target.value);
        setFieldValue(field, capitalizedValue);
    };

    const { errors, touched, values, isSubmitting, handleSubmit, getFieldProps, setSubmitting, setFieldValue, handleChange } = formik;

    return (
        <Page title="Add Company Member">
            <Container maxWidth="xl">
                <FormikProvider value={formik}>
                    <Form 
                        autoComplete="off" 
                        noValidate 
                        onSubmit={handleSubmit} 
                        onKeyDown={(keyEvent) => {
                            if ((keyEvent.key === 'Enter')) {
                                keyEvent.preventDefault();
                            }
                        }}
                    >
                        <Card sx={{ mt: 2 }} variant="outlined">
                            <CardContent sx={{mt:2}}>
                                <Stack spacing={2}>
                                    {/* Input fields for adding a new company member */}
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="First Name"
                                            required
                                            {...getFieldProps('first_name')}
                                            error={Boolean(touched.first_name && errors.first_name)}
                                            helperText={touched.first_name && errors.first_name}
                                            onChange={handleNameChange("first_name")}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Middle Name"
                                            {...getFieldProps('middle_name')}
                                            error={Boolean(touched.middle_name && errors.middle_name)}
                                            helperText={touched.middle_name && errors.middle_name}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Last Name"
                                            required
                                            {...getFieldProps('last_name')}
                                            error={Boolean(touched.last_name && errors.last_name)}
                                            helperText={touched.last_name && errors.last_name}
                                            onChange={handleNameChange("last_name")}
                                        />
                                    </Stack>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            required
                                            {...getFieldProps('email')}
                                            error={Boolean(touched.email && errors.email)}
                                            helperText={touched.email && errors.email}
                                        />
                                        {locationData?.length > 0 && (
                                            <Autocomplete
                                                fullWidth
                                                options={locationData}
                                                getOptionLabel={(option) => option.label || ''}
                                                value={locationData.find((c) => c.id === formik.values?.country) || null}
                                                onChange={(event, value) => {
                                                    formik.setFieldValue('country', value?.id || '');
                                                    // Update phone country code when country changes
                                                    if (value?.label) {
                                                        const isoCode = getCountryISOCode(value.label);
                                                        setPhoneCountryCode(isoCode);
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Country"
                                                        error={Boolean(touched?.country && errors?.country)}
                                                        helperText={touched?.country && errors?.country}
                                                    />
                                                )}
                                            />
                                        )}
                                    </Stack>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        <Stack width='100%' height='100%'>
                                            <PhoneInput
                                                country={phoneCountryCode}
                                                value={formik.values.mobile}
                                                onChange={(value) => formik.setFieldValue('mobile', value)}
                                                inputProps={{
                                                    name: 'Mobile',
                                                    required: true,
                                                }}
                                                inputStyle={{ width: '100%', height: '3.3rem', borderRadius: '8px', border: '1px solid #ccc', fontFamily:"Lexend" }}
                                                buttonStyle={{
                                                    borderRadius: '8px 0px 0px 8px',
                                                    backgroundColor: 'transparent',
                                                    borderColor: 'transparent'
                                                }}
                                                placeholder=''
                                                autoFormat={true}
                                            />
                                            <ErrorMessage name="mobile">
                                                {(msg) => <span
                                                    style={{ width: "100%", color: "#FF4842", fontSize: "14px", textAlign: "center" }}>
                                                    {msg}
                                                </span>}
                                            </ErrorMessage>
                                        </Stack>
                                    </Stack>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="LinkedIn Profile"
                                            required
                                            {...getFieldProps('linkedin_profile')}
                                            error={Boolean(touched.linkedin_profile && errors.linkedin_profile)}
                                            helperText={touched.linkedin_profile && errors.linkedin_profile}
                                        />
                                        <Autocomplete
                                            multiple
                                            fullWidth
                                            id="tags-outlined-secondary"
                                            options={cmdData}
                                            getOptionLabel={(option) => option.label}
                                            onChange={(event, value) => setFieldValue("cmd", value.map(obj => obj.id))}
                                            defaultValue={cmdData.filter(obj => values.cmd.includes(obj.id))}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    required
                                                    variant="outlined"
                                                    label="Designation"
                                                    error={Boolean(touched.cmd && errors.cmd)}
                                                    helperText={touched.cmd && errors.cmd}
                                                />
                                            )}
                                        />
                                    </Stack>
                                    <Stack>
                                    <div role="group" style={{ width: '40%' }}>
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            spacing={3}
                                            style={{ padding: "20px 0 0 12px" }}
                                        >
                                            <div id="switch-label" style={{ color: '#637381', marginTop: '5px' }}> Is Active? </div>
                                            <Switch
                                                name="is_active"
                                                value="true"
                                                onChange={(event, checked) => {
                                                    setFieldValue("is_active", checked ? "true" : "false");
                                                }}
                                            />
                                        </Stack>
                                        <ErrorMessage name="is_active">
                                            {(msg) => <span style={{ color: "#FF4842", fontSize: "14px", marginTop: "8px" }}>
                                                {msg}
                                            </span>}
                                        </ErrorMessage>
                                    </div>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }} />
                        <LoadingButton
                            size="large"
                            type="submit"
                            variant="contained"
                            loading={isSubmitting}
                            style={{ display: "block", margin: "auto", width: 300 }}
                        >
                            Add Company Member
                        </LoadingButton>
                    </Form>
                </FormikProvider>
            </Container>
        </Page >
    )
}